/**
 * Client for the public CRM intake endpoint.
 *
 * The site is static — there is no backend of its own. Submissions go to the
 * `AdminPublic` Lambda in admin.10sp.us (`auth_mode = 'none'`), which owns the
 * CRM tables. That handler resolves which site is posting from the request's
 * own Origin, so nothing here needs to prove its identity — but it does mean
 * only the allow-listed hosts can submit.
 */

const ENDPOINTS = {
  demo: 'https://opb24spbfl.execute-api.us-west-2.amazonaws.com/demo/public',
  production: 'https://rv5fd68nq3.execute-api.us-west-2.amazonaws.com/production/public',
};

// Anything that isn't the production hostname talks to demo, so a developer or
// a preview build can never write leads into the production CRM by accident.
const PRODUCTION_HOSTS = [
  'tenspacesoftware.com',
  'www.tenspacesoftware.com',
  '10spacesoftware.com',
  'www.10spacesoftware.com',
];

export function resolveEndpoint(hostname) {
  if (import.meta.env.VITE_CONTACT_API) return import.meta.env.VITE_CONTACT_API;
  const host = (
    hostname ||
    (typeof window !== 'undefined' ? window.location.hostname : '') ||
    ''
  ).toLowerCase();
  return PRODUCTION_HOSTS.includes(host) ? ENDPOINTS.production : ENDPOINTS.demo;
}

export class ContactApiError extends Error {
  constructor(message, field) {
    super(message);
    this.name = 'ContactApiError';
    this.field = field;
  }
}

const GENERIC_FAILURE =
  "We couldn't send that just now. Please try again, or email support@10spacesoftware.com.";

/**
 * Submit one contact form.
 *
 * Resolves with the server's message on success. Rejects with a
 * `ContactApiError` carrying the offending field name when the server rejects
 * the submission, so the form can mark the right input.
 */
export async function submitContactForm(payload, { endpoint, fetchImpl } = {}) {
  const request = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (!request) throw new ContactApiError(GENERIC_FAILURE);

  let response;
  try {
    response = await request(endpoint || resolveEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'contact-submit', payload }),
    });
  } catch (error) {
    // Network failure, DNS, CORS preflight — the visitor can't act on any of
    // the detail, so it does not reach them.
    throw new ContactApiError(GENERIC_FAILURE);
  }

  let body = {};
  try {
    body = await response.json();
  } catch (error) {
    body = {};
  }

  if (!response.ok || body.ok === false) {
    throw new ContactApiError(body.message || GENERIC_FAILURE, body.field);
  }
  return body.message || 'Thanks — your message is on its way.';
}
